/**
 * <name-patch>
 *
 * Champ "targhetta personalizzata" du template product.harnais-new.
 *
 * Les inputs cachés du bloc sont rendus SANS attribut `name`. Ce composant
 * pose le `name` uniquement quand le champ contient du texte, et le retire
 * dès qu'il est vidé. C'est ce qui garantit que :
 *   - un champ vide n'ajoute aucune propriété vide au panier ;
 *   - un champ vide ne facture pas la targhetta.
 *
 * `assets/product-form.js` lit ensuite ces inputs au moment du submit :
 *   properties[<property_name>]        -> propriété sur la ligne harnais
 *   items[<n>][id] / [quantity]        -> ligne targhetta facturée
 *   items[<n>][properties][<name>]     -> nom répété sur la ligne targhetta
 */
class NamePatch extends HTMLElement {
  constructor() {
    super();

    this.input = null;
    this.counter = null;
    this.propertyInput = null;
    this.itemIdInput = null;
    this.itemQuantityInput = null;
    this.itemPropertyInput = null;

    this.sync = this.sync.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('[data-ref="name-patch-input"]');
    this.counter = this.querySelector('[data-ref="name-patch-counter"]');
    this.propertyInput = this.querySelector('[data-ref="name-patch-property"]');
    this.itemIdInput = this.querySelector('[data-ref="name-patch-item-id"]');
    this.itemQuantityInput = this.querySelector('[data-ref="name-patch-item-quantity"]');
    this.itemPropertyInput = this.querySelector('[data-ref="name-patch-item-property"]');

    if (!this.input || !this.propertyInput) return;

    this.propertyName = this.dataset.propertyName || 'Nome';
    this.itemIndex = parseInt(this.dataset.itemIndex, 10);
    if (Number.isNaN(this.itemIndex)) this.itemIndex = 1;
    this.maxLength = parseInt(this.dataset.maxLength, 10) || 15;

    if (!this.itemIdInput) {
      console.warn(
        "name-patch : aucun ID de variante configuré, la targhetta ne sera pas facturée. " +
          'Renseignez « ID de variante » dans les réglages du bloc.'
      );
    }

    this.input.addEventListener('input', this.sync);
    this.input.addEventListener('change', this.sync);

    // Filet de sécurité : on resynchronise juste avant l'envoi du formulaire,
    // au cas où le navigateur aurait autorempli le champ sans événement.
    // En phase de capture sur `document` pour passer AVANT le handler de
    // product-form.js, qui lit les inputs de façon synchrone.
    document.addEventListener('submit', this.sync, true);

    this.sync();
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.sync);
    this.input?.removeEventListener('change', this.sync);
    document.removeEventListener('submit', this.sync, true);
  }

  /** Nettoie la saisie : espaces multiples réduits, trim, longueur max. */
  get value() {
    return (this.input?.value || '').replace(/\s+/g, ' ').trim().slice(0, this.maxLength);
  }

  sync() {
    const value = this.value;
    const filled = value.length > 0;

    this.dataset.filled = filled ? 'true' : 'false';

    if (this.counter) {
      this.counter.textContent = `${value.length}/${this.maxLength}`;
    }

    // Propriété posée sur la ligne du harnais.
    this.#toggleInput(this.propertyInput, filled, `properties[${this.propertyName}]`, value);

    // Ligne supplémentaire facturée pour la targhetta.
    this.#toggleInput(this.itemIdInput, filled, `items[${this.itemIndex}][id]`, this.itemIdInput?.value);
    this.#toggleInput(this.itemQuantityInput, filled, `items[${this.itemIndex}][quantity]`, '1');
    this.#toggleInput(
      this.itemPropertyInput,
      filled,
      `items[${this.itemIndex}][properties][${this.propertyName}]`,
      value
    );
  }

  /**
   * Ajoute ou retire l'attribut `name` d'un input caché.
   * Sans `name`, l'input est invisible pour product-form.js.
   *
   * @param {HTMLInputElement|null} input
   * @param {boolean} enabled
   * @param {string} name
   * @param {string|undefined} value
   */
  #toggleInput(input, enabled, name, value) {
    if (!input) return;

    if (enabled) {
      if (value !== undefined) input.value = value;
      input.setAttribute('name', name);
    } else {
      input.removeAttribute('name');
    }
  }
}

if (!customElements.get('name-patch')) {
  customElements.define('name-patch', NamePatch);
}
